import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { fetchContributors, createContributor, updateContributor, deleteContributor } from "../services/api";
import { ArrowLeft, Plus, Pencil, Trash2, Loader2, Users, X, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveAvatar = (avatar) => {
  if (!avatar) return "";
  if (avatar.startsWith("http")) return avatar;
  return `${BACKEND_URL}${avatar}`;
};

const ContributorForm = ({ initial, onSave, onCancel, loading }) => {
  const [form, setForm] = useState({
    name: initial?.name || "",
    role: initial?.role || "",
    bio: initial?.bio || "",
    avatar_url: initial?.avatar || "",
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [preview, setPreview] = useState(initial?.avatar ? resolveAvatar(initial.avatar) : "");
  const fileRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("role", form.role);
    fd.append("bio", form.bio);
    if (avatarFile) {
      fd.append("avatar", avatarFile);
    } else if (form.avatar_url) {
      fd.append("avatar_url", form.avatar_url);
    }
    onSave(fd);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
      <div className="flex items-center gap-4">
        <div
          className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden cursor-pointer border-2 border-dashed border-gray-200 hover:border-[#1B7A3D] transition-colors flex-shrink-0"
          onClick={() => fileRef.current?.click()}
        >
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover" />
          ) : (
            <Upload size={20} className="text-gray-400" />
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
        <div className="flex-1 space-y-3">
          <input
            data-testid="contributor-name-input"
            type="text"
            placeholder="Name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D]"
            required
          />
          <input
            data-testid="contributor-role-input"
            type="text"
            placeholder="Role (e.g. Football Correspondent)"
            value={form.role}
            onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D]"
          />
        </div>
      </div>
      <textarea
        data-testid="contributor-bio-input"
        placeholder="Short bio (optional)"
        value={form.bio}
        onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
        rows={2}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1B7A3D]/20 focus:border-[#1B7A3D] resize-none"
      />
      <div className="flex items-center gap-2 justify-end">
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-50 rounded-lg transition-colors">
            Cancel
          </button>
        )}
        <button
          data-testid="contributor-save-btn"
          type="submit"
          disabled={loading || !form.name}
          className="px-5 py-2 bg-[#1B7A3D] hover:bg-[#15612F] disabled:bg-gray-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
        >
          {loading && <Loader2 className="animate-spin" size={14} />}
          {initial ? "Update" : "Add Contributor"}
        </button>
      </div>
    </form>
  );
};

const Contributors = () => {
  const [contributors, setContributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchContributors();
      setContributors(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (fd) => {
    setSaving(true);
    try {
      await createContributor(fd);
      setShowAdd(false);
      await load();
    } catch (err) {
      alert("Failed to create contributor");
    }
    setSaving(false);
  };

  const handleUpdate = async (id, fd) => {
    setSaving(true);
    try {
      await updateContributor(id, fd);
      setEditingId(null);
      await load();
    } catch (err) {
      alert("Failed to update contributor");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contributor?")) return;
    try {
      await deleteContributor(id);
      await load();
    } catch {
      alert("Failed to delete");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <div className="flex items-center gap-2">
              <Users size={22} className="text-[#1B7A3D]" />
              <h1 data-testid="contributors-page-title" className="text-2xl font-black text-gray-900">Contributors</h1>
            </div>
          </div>
          {!showAdd && (
            <button
              data-testid="add-contributor-btn"
              onClick={() => setShowAdd(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1B7A3D] hover:bg-[#15612F] text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <Plus size={16} />
              Add
            </button>
          )}
        </div>

        {showAdd && (
          <div className="mb-6">
            <ContributorForm onSave={handleCreate} onCancel={() => setShowAdd(false)} loading={saving} />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="animate-spin text-[#1B7A3D]" size={28} />
          </div>
        ) : contributors.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Users className="mx-auto text-gray-300 mb-4" size={48} />
            <h2 className="text-lg font-bold text-gray-900 mb-2">No Contributors Yet</h2>
            <p className="text-sm text-gray-500">Add your first contributor profile above.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {contributors.map((c) =>
              editingId === c.id ? (
                <ContributorForm
                  key={c.id}
                  initial={c}
                  onSave={(fd) => handleUpdate(c.id, fd)}
                  onCancel={() => setEditingId(null)}
                  loading={saving}
                />
              ) : (
                <div
                  key={c.id}
                  data-testid={`contributor-${c.id}`}
                  className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4 hover:shadow-md transition-shadow"
                >
                  <img
                    src={resolveAvatar(c.avatar)}
                    alt={c.name}
                    className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900">{c.name}</h3>
                    <p className="text-sm text-gray-500">{c.role}</p>
                    {c.bio && <p className="text-xs text-gray-400 mt-1 truncate">{c.bio}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setEditingId(c.id)}
                      className="p-2 rounded-lg hover:bg-green-50 text-gray-400 hover:text-[#1B7A3D] transition-all"
                      title="Edit"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-all"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Contributors;
